import { useMemo } from 'react'

import { isCurrentRequestBot } from 'actions/SystemActions'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'

/**
 * The offer pages the metasearch first tile can land on: `/partner/...` for a bedbank
 * property, `/offer/...` (SPP) when the property resolves to an LE offer — in production
 * most first-tile clicks land on the SPP.
 */
const ENTRY_OFFER_PAGE_PATHS = [
  '/:regionCode/partner/:offerSlug/:offerId',
  '/:regionCode/offer/:offerSlug/:offerId',
  '/:regionCode/offer/:offerId',
]

/**
 * Query param the metasearch page appends to the first tile's offer URL — the explicit
 * "reached this offer page by clicking the first tile" signal the experiment keys on.
 *
 * It must live on the URL (not in memory or a cookie): in production the tile opens the
 * offer page in a new tab with a full page load (`OPEN_NEW_TAB_OFFER_CLICK`), so nothing
 * else reliably survives the hop. Secondary metasearch tiles never carry it.
 */
export const METASEARCH_LPP_PROMO_ENTRY_PARAM = 'msTopTile'

const PROMO_UTM_SOURCE = 'google'
const PROMO_UTM_MEDIUM = 'cpc'

/**
 * The funded Google CPC campaigns, hardcoded for the life of the experiment. Traffic from any
 * other campaign is never bucketed and never activated, so the Optimizely results only ever
 * contain funded sessions.
 *
 * UTMs are read from `state.utm`. The metasearch tile forwards the full core UTM set
 * (source/medium/campaign/content) onto the offer URL — forwarding `utm_content` alone
 * would make `processUtmParameters` replace the whole `utmsV2` cookie with just that key
 * and wipe the campaign this experiment filters on. Direct offer-page visits without the
 * forwarded params still back-fill from the `utmsV2` cookie.
 */
const PROMO_UTM_CAMPAIGNS = new Set([
  '22890614366',
  '22890614363',
  '22890614369',
  '24064658325',
])

/**
 * Identifies the experiment's own promo display configs by the funded campaign they target.
 *
 * Keyed on `PROMO_UTM_CAMPAIGNS` — the same set that defines eligibility — so the experiment
 * has a single hardcoded identity, and the gate survives the promo code being renamed or
 * recreated (svc-promo also clones per-user child codes with a suffix, so code names are not
 * a stable identifier).
 *
 * Non-experiment traffic must never see these configs: svc-promo's config listing treats
 * missing UTM params as wildcards, so organic or partially-attributed traffic still receives
 * the experiment's UTM-targeted configs and would otherwise leak the promo outside the
 * experiment.
 */
export function isLppPromoExperimentConfig(promoConfig: App.PromoDisplayConfig): boolean {
  return !!promoConfig.utmCampaign && PROMO_UTM_CAMPAIGNS.has(promoConfig.utmCampaign)
}

export interface MetasearchLppPromoExperiment {
  /**
   * The session reached this offer page by clicking the metasearch first tile (entry param
   * present) and matches the funded Google CPC campaign filters. Only this traffic is ever
   * bucketed and activated, and only this traffic has its promo popups governed by the
   * experiment — `isEligible && !isVariant` (the control arm) must see no promo popup at
   * all, so the arms compare "LPP promo vs no promo".
   */
  readonly isEligible: boolean
  /** Eligible traffic bucketed into a treatment variation. Gate the promo on this. */
  readonly isVariant: boolean
}

/**
 * Resolves the `growth_metasearch_lpp_promo` experiment on the offer page reached from the
 * metasearch first tile.
 *
 * Entry is the explicit first-tile click signal (`msTopTile=1` on the offer URL), not mere
 * page presence: direct/bookmarked/shared offer links and GHA direct-to-offer redirects never
 * carry it, so they are excluded by construction. The UTM filters then scope entry to the
 * funded campaigns, and bots, the GHA price verifier, and Hotel Center price-check sessions
 * are excluded explicitly.
 */
function useMetasearchLppPromoExperiment(): MetasearchLppPromoExperiment {
  // Read from the store rather than `useLocation` so this hook carries no Router-context
  // requirement — it is called from deep offer-page callbacks as well as page components.
  const pathname = useAppSelector(state => state.router.location.pathname)
  const hasEntryParam = useAppSelector(
    state => state.router.location.query?.[METASEARCH_LPP_PROMO_ENTRY_PARAM] === '1',
  )
  // GHA price scraper / Hotel Center price-check sessions follow the exact metasearch →
  // offer-page flow (and would carry the entry param); they must never enter the experiment.
  const isPriceCheckSession = useAppSelector(state => !!state.router.location.query?.priceCheck)
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const utmCampaign = useAppSelector(state => state.utm.campaign)
  const isBotRequest = useAppSelector(isCurrentRequestBot)
  const isGhaVerifier = useAppSelector(state =>
    state.system.rawUserAgentString.includes('Google-HotelAdsVerifier'),
  )

  const isEntryOfferPage = useMemo(
    () => !!matchLocaleAwarePath(pathname, { path: ENTRY_OFFER_PAGE_PATHS, exact: true }),
    [pathname],
  )

  const isEligible = (
    config.BRAND === 'luxuryescapes' &&
    isEntryOfferPage &&
    hasEntryParam &&
    !isPriceCheckSession &&
    utmSource === PROMO_UTM_SOURCE &&
    utmMedium === PROMO_UTM_MEDIUM &&
    PROMO_UTM_CAMPAIGNS.has(utmCampaign) &&
    !isBotRequest &&
    !isGhaVerifier
  )

  const { isAnyVariant } = useExperiment(
    Experiments.growthMetasearchLppPromo,
    { canActivate: isEligible },
  )

  return useMemo(() => ({
    isEligible,
    isVariant: isEligible && isAnyVariant,
  }), [isEligible, isAnyVariant])
}

export default useMetasearchLppPromoExperiment
